import { isEmpty } from "lodash"
import {
  ManagedProvider,
  Provider,
  ProviderActivationData,
  ProviderNameResult,
  ProviderStatusDisplayId,
} from "../../../domain/Provider/schema"
import appInsightsLogger from "../../logging/appInsightsLogger"
import {
  buildUrl,
  buildURLWithQueryParams,
  getData,
  getFile,
  PagedResult,
  postData,
  postFormData,
  putData,
  postDataFlexibleTyped,
} from "../apiService"

const postProvider = async (payload: Partial<Provider>): Promise<Provider> => {
  const url = apiRoutes.post.PROVIDER
  return postDataFlexibleTyped(url, payload)
}

const postLinkUserToProvider = async (providerId: string) => {
  const url = buildUrl(apiRoutes.post.LINK_USER_TO_PROVIDER, {
    providerId,
  })
  return putData<Provider>(url, { providerId })
}

const postNotifyInsuranceRequired = async (
  practiceName: string,
  locationIds: string[],
): Promise<void> => {
  if (isEmpty(locationIds)) return Promise.reject()
  const url = apiRoutes.post.NOTIFY_INSURANCE_REQUIRED
  return postData(url, { practiceName, locationIds })
}

const apiRoutes = {
  get: {
    PROVIDER_ACTIVATION_DATA: "/provider/providers/activationData",
    PROVIDER_BY_ID_ROUTE: "/provider/providers/:providerId",
    PROVIDER_BY_USER_ID_ROUTE: "/provider/providers/user/:userId",
    PROVIDER_NAMES: "/provider/providers",
    PROVIDER_FILE_BY_FILE_ID_ROUTE:
      "/provider/providers/:providerId/files/:fileId/content",
    PROVIDER_DISCLOSURE_DOC_ROUTE:
      "/provider/providers/:providerId/files/:fileId/content",
    PROVIDER_COUNT_BY_LOCATION_ID:
      "/provider/providers/locations/:locationId/count",
  },
  post: {
    PROVIDER: "/provider/providers",
    PROVIDERS_FROM_USER_IDS_ROUTE: "/provider/providers/users/public",
    PROVIDER_BY_USER_IDS_BATCH_ROUTE: "/provider/providers/users/batch",
    PROVIDER_FILE_ROUTE: "/provider/providers/:providerId/files",
    LINK_USER_TO_PROVIDER: "/provider/providers/detached/:providerId",
    PUBLIC_PROVIDERS_BY_ID_ROUTE: "/provider/providers/public",
    NOTIFY_INSURANCE_REQUIRED:
      "/provider/liabilityInsurances/notifyNowRequired",
    PROVIDERS_MIGRATION: "/provider/providers/migration",
  },
  put: {
    PROVIDER_ROUTE: "/provider/providers/:providerId",
    PROVIDER_STATUS_ROUTE: "/provider/providers/:providerId/status",
    RESET_PROVIDER_DISCLOSURE:
      "/provider/providers/:providerId/disclosure/reset",
  },
}

const providerActivationData = async ({
  pageNumber,
  pageSize,
}: {
  pageNumber: string
  pageSize: string
}) => {
  return getData<PagedResult<ProviderActivationData>>(
    buildURLWithQueryParams(apiRoutes.get.PROVIDER_ACTIVATION_DATA, undefined, {
      pageNumber,
      pageSize,
    }),
  )
}

const providerById = async ({ providerId }: { providerId: string }) => {
  return getData<Provider>(
    buildUrl(apiRoutes.get.PROVIDER_BY_ID_ROUTE, { providerId }),
  )
}

const providerByUserId = async ({ userId }: { userId: string }) => {
  return getData<Provider>(
    buildUrl(apiRoutes.get.PROVIDER_BY_USER_ID_ROUTE, { userId }),
  )
}

const providersByUserIdsBatch = async ({ userIds }: { userIds: string[] }) => {
  return getData<Provider[]>(
    buildUrl(apiRoutes.post.PROVIDER_BY_USER_IDS_BATCH_ROUTE),
    true,
    userIds,
  )
}

const providerCountByLocationId = async ({
  locationId,
}: {
  locationId: string
}) => {
  return getData<number>(
    buildURLWithQueryParams(apiRoutes.get.PROVIDER_COUNT_BY_LOCATION_ID, {
      locationId,
    }),
  )
}

const providerDisclosureDocument = async ({
  providerId,
  fileId,
}: {
  providerId: string
  fileId: string
}) => {
  return getFile<Blob>(
    buildUrl(apiRoutes.get.PROVIDER_DISCLOSURE_DOC_ROUTE, {
      providerId,
      fileId,
    }),
    undefined,
    undefined,
    true,
  )
}

const providerFileByFileId = async (providerId: string, fileId: string) => {
  return getFile<File>(
    buildUrl(apiRoutes.get.PROVIDER_FILE_BY_FILE_ID_ROUTE, {
      providerId,
      fileId,
    }),
    undefined,
    undefined,
    true,
  )
}

const providerNamesByStatus = async ({ status }: { status: string }) => {
  return getData<ProviderNameResult[]>(
    buildURLWithQueryParams(apiRoutes.get.PROVIDER_NAMES, undefined, {
      statusFilter: status,
    }),
  )
}

const providersFromUserIds = async ({ userIds }: { userIds: string[] }) => {
  return getData<Provider[]>(
    apiRoutes.post.PROVIDERS_FROM_USER_IDS_ROUTE,
    true,
    userIds,
  )
}

const postProviderManaged = async (
  payload: ManagedProvider,
): Promise<Provider> => {
  const url = apiRoutes.post.PROVIDER
  return postDataFlexibleTyped(url, payload)
}

const publicProvidersByIds = async (
  providerIds: string[],
): Promise<Provider[]> => {
  let providers: Provider[] = []

  try {
    providers = await getData<Provider[]>(
      apiRoutes.post.PUBLIC_PROVIDERS_BY_ID_ROUTE,
      true,
      providerIds,
    )
  } catch (error) {
    appInsightsLogger.exception(error as Error)
    throw new Error(
      `Failed to fetch public providers by providerIds:\n${error}`,
    )
  }

  return providers
}

const putProvider = async (providerId: string, provider: Provider) => {
  return putData<Provider>(
    buildUrl(apiRoutes.put.PROVIDER_ROUTE, { providerId }),
    provider,
  )
}

const putProviderStatus = async ({
  providerId,
  providerStatusDisplayId,
  deactivationReasonId,
}: {
  providerId: string
  providerStatusDisplayId: ProviderStatusDisplayId
  deactivationReasonId?: number
}) => {
  return putData<Provider>(
    buildURLWithQueryParams(
      apiRoutes.put.PROVIDER_STATUS_ROUTE,
      { providerId },
      { "api-version": "2.0" },
    ),
    {
      providerStatusDisplayId,
      ...(deactivationReasonId !== undefined ? { deactivationReasonId } : {}),
    },
  )
}

const putProviderActivation = async (providerId: string) => {
  const provider = await providerById({ providerId })
  if (!provider) {
    throw new Error("Provider not found")
  }
  provider.providerStatusDisplayId = "active"
  return putProvider(providerId, provider)
}

const putProviderDisclosureReset = async (providerId: string) => {
  return putData(
    buildUrl(apiRoutes.put.RESET_PROVIDER_DISCLOSURE, { providerId }),
  )
}

export interface ProviderFileData {
  id: string
  fileName: string
  fileStatusDisplayId: string
}

const postProviderFile = async (
  providerId: string,
  file: Blob | null,
  fileName: string,
  entityId: string,
  entityType: string,
) => {
  const formData = new FormData()
  formData.append("FileName", fileName)
  formData.append("EntityType", entityType)
  formData.append("EntityId", entityId)
  if (file) {
    formData.append("FileData", file, fileName)
  }
  return postFormData<ProviderFileData>(
    buildUrl(apiRoutes.post.PROVIDER_FILE_ROUTE, { providerId }),
    formData,
  )
}

export const providerApi = {
  get: {
    PROVIDER_ACTIVATION_DATA: providerActivationData,
    PROVIDER_BY_ID: providerById,
    PROVIDER_BY_USER_ID: providerByUserId,
    PROVIDERS_BY_USER_IDS_BATCH: providersByUserIdsBatch,
    PROVIDER_COUNT_BY_LOCATION_ID: providerCountByLocationId,
    PROVIDER_DISCLOSURE_DOC: providerDisclosureDocument,
    PROVIDER_FILE: providerFileByFileId,
    PROVIDER_NAMES: providerNamesByStatus,
    PUBLIC_PROVIDERS_BY_ID: publicProvidersByIds,
  },
  post: {
    LINK_USER_TO_PROVIDER: postLinkUserToProvider,
    NOTIFY_INSURANCE_REQUIRED: postNotifyInsuranceRequired,
    PROVIDER: postProvider,
    PROVIDER_FILE: postProviderFile,
    PROVIDERS_FROM_USER_IDS: providersFromUserIds,
    PROVIDER_MANAGED: postProviderManaged,
  },
  put: {
    ACTIVATE_PROVIDER: putProviderActivation,
    PROVIDER: putProvider,
    PROVIDER_STATUS: putProviderStatus,
    RESET_PROVIDER_DISCLOSURE: putProviderDisclosureReset,
  },
}
